import { h } from "vue";
import { t } from "#ce/apps/SpAdmin/SpSlotInspectorApp/i18n";
import SlotInspectorStatusTagGroup from "#ce/apps/SpAdmin/SpSlotInspectorApp/components/cells/SlotInspectorStatusTagGroup.ce.vue";

const SECONDARY_TEXT_STYLE = Object.freeze({
  color: "var(--sp-sys-color-neutral-secondary)",
  fontWeight: "var(--sp-sys-font-weight-light)",
});

const NOT_IN_SPARKLE_STYLE = Object.freeze({
  "--sp-comp-icon-fill": "var(--sp-sys-color-neutral-secondary)",
  display: "inline-flex",
  marginInlineStart: "var(--sp-ref-scale-8)",
  verticalAlign: "middle",
});

const VISUALLY_HIDDEN_STYLE = Object.freeze({
  position: "absolute",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
});

/**
 * Returns the column descriptors of a department's visit-type table: Visit type, EHR ID and Result.
 *
 * @returns {Array<Object>}
 */
export function createVisitTypeColumns() {
  return [
    // `label` is the first column, so the row toggle is named after it.
    { field: "label", label: t("column_visit_type"), formatter: renderVisitTypeName },
    { field: "ehrId", label: t("column_ehr_id"), width: "var(--sp-ref-scale-96)", formatter: renderEhrId },
    { field: "counts", label: t("column_result"), width: "var(--sp-ref-scale-160)", formatter: renderResult },
  ];
}

function renderVisitTypeName(_label, row) {
  if (!row.idSuffix) {
    return [row.name, renderNotInSparkle()];
  }

  return [row.name, h("span", { style: SECONDARY_TEXT_STYLE }, ` · ${row.idSuffix}`)];
}

// The spacing sits on a wrapper because sp-tooltip copies its own `style` onto its inner element.
function renderNotInSparkle() {
  return h("span", { style: NOT_IN_SPARKLE_STYLE }, [
    h("sp-tooltip", { content: t("not_in_sparkle"), position: "right", theme: "dark" }, [
      h("sp-icon", { name: "info-filled", size: "x-small" }),
      h("span", { style: VISUALLY_HIDDEN_STYLE }, t("not_in_sparkle_label")),
    ]),
  ]);
}

function renderEhrId(ehrId) {
  return h("span", { style: SECONDARY_TEXT_STYLE }, ehrId);
}

function renderResult(counts) {
  return h(SlotInspectorStatusTagGroup, { counts });
}

/**
 * Returns a section's visit-type groups as table rows. Each row's `label` names it: the visit type's name, followed
 * by its Sparkle id when Sparkle has the visit type, as `"Returning Patient Sports Medicine #164"`. Such a row also
 * carries the `#164` part as `idSuffix`. A visit type Sparkle doesn't have shows no id, and its column shows an info icon instead. A group the EHR sent no name
 * for shows "Unknown visit type", and its `label` adds the EHR ID: `"Unknown visit type 9001"`.
 *
 * @param {Array<Object>} visitTypes - A payload section's `visitTypes`.
 * @returns {Array<Object>}
 */
export function visitTypeRows(visitTypes) {
  return visitTypes.map((visitType) => {
    if (!visitType.name) {
      const name = t("unknown_visit_type");

      return { ...visitType, name, label: `${name} ${visitType.ehrId}`, idSuffix: null };
    }

    const name = visitType.name;

    if (visitType.visitTypeId == null) {
      return { ...visitType, label: name, idSuffix: null };
    }

    const idSuffix = t("visit_type_id", { id: visitType.visitTypeId });

    return { ...visitType, label: `${name} ${idSuffix}`, idSuffix };
  });
}
